import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Microphone, HardDrives, BellRinging, Database, ShieldCheck, GlobeHemisphereWest } from "@phosphor-icons/react";

const DATA = [
  {
    icon: Camera,
    label: "Camera",
    used_for: "Capturing photos and videos you choose to upload, going Live, and showing live previews.",
    shared: "Posted media is delivered through our CDN (Mux for video, Cloudinary for images). Never shared with advertisers.",
    optional: true,
  },
  {
    icon: Microphone,
    label: "Microphone",
    used_for: "Recording audio for your videos and Live broadcasts (including in-app voice chat with viewers).",
    shared: "Audio is part of your video stream and follows the same delivery as Camera.",
    optional: true,
  },
  {
    icon: HardDrives,
    label: "Photos & files",
    used_for: "Uploading existing photos or videos from your device to Giffwurldaunce.",
    shared: "Only files you explicitly select are uploaded. Read-only access; we never write to your library.",
    optional: true,
  },
  {
    icon: BellRinging,
    label: "Notifications",
    used_for: "Telling you about new followers, gifts, comments, messages, and lives from creators you follow.",
    shared: "Push delivery is handled by OneSignal. You can mute every category in Settings.",
    optional: true,
  },
  {
    icon: GlobeHemisphereWest,
    label: "Approximate location (optional)",
    used_for: "Letting you tag a city/country on a post if you choose to. Never collected automatically.",
    shared: "Stored only on posts where you typed a location yourself. Visible to anyone who can see the post.",
    optional: true,
  },
  {
    icon: Database,
    label: "Account info",
    used_for: "Your email, display name, bio, country, follow graph, posts, likes, comments, wallet balance, and PayPal order IDs.",
    shared: "Stored in our private MongoDB. Payment processing routed through PayPal — we never see your card details.",
    optional: false,
  },
];

export default function DataSafety() {
  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="data-safety-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
        <ArrowLeft size={14} /> Settings
      </Link>
      <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Transparency</p>
      <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Data safety.</h1>
      <p className="text-cream/65 text-sm mt-3 leading-relaxed">
        Exactly what we ask for, why, and where it goes. Nothing hidden. You can revoke any permission in your device settings at any time.
      </p>

      <div className="mt-6 rounded-3xl bg-jam-green/10 border border-jam-green/30 p-4 flex items-start gap-3">
        <ShieldCheck size={22} weight="duotone" className="text-jam-green flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-cream font-black text-sm">We don't sell your data.</p>
          <p className="text-cream/65 text-xs mt-1 leading-relaxed">
            Giffwurldaunce never sells personal information to data brokers, advertisers, or AI training pipelines.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {DATA.map(({ icon: I, label, used_for, shared, optional }) => (
          <div key={label} className="rounded-3xl bg-white/5 border border-white/10 p-4" data-testid={`data-safety-${label.toLowerCase().replace(/\W+/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-papaya/10 text-papaya flex items-center justify-center flex-shrink-0">
                <I size={22} weight="duotone" />
              </div>
              <div>
                <p className="text-cream font-black text-base">{label}</p>
                {optional && <p className="text-jam-green text-[10px] tracking-[0.25em] uppercase font-bold">Optional — you control it</p>}
                {!optional && <p className="text-papaya text-[10px] tracking-[0.25em] uppercase font-bold">Required for account</p>}
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-xs leading-relaxed">
              <p className="text-cream/85"><span className="text-cream/50 font-bold uppercase tracking-[0.18em] text-[10px] block mb-0.5">Used for</span>{used_for}</p>
              <p className="text-cream/85 pt-1"><span className="text-cream/50 font-bold uppercase tracking-[0.18em] text-[10px] block mb-0.5">Where it goes</span>{shared}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-8 text-cream/40 text-[11px] leading-relaxed">
        Full details are in our <Link to="/privacy" className="text-papaya">Privacy Policy</Link> and{" "}
        <Link to="/terms" className="text-papaya">Terms of Service</Link>. To delete your account and erase every record we hold, go to Settings →
        Delete account, or email <span className="text-papaya">privacy@giffwurldaunce.com</span>.
      </p>
    </div>
  );
}
